import React, {
  createContext,
  useState,
  useContext,
  useRef,
} from "react";
import { useSelector } from "react-redux";
import * as billing from "../api/billing/index";
import { SUPPORTEDLANGCODES } from "../constants/appConstants";
import PropTypes from "prop-types";

const CountryListContext = createContext();

// Custom hook to use the context
export const useCountryListContext = () => {
  const context = useContext(CountryListContext);
  if (!context) {
    throw new Error(
      "useCountryListContext must be used within a CountryListProvider"
    );
  }
  return context;
};

// Provider component
export const CountryListProvider = ({ children, regionId }) => {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [countryList, setCountryList] = useState([]); // Global array
  const previousRegionId = useRef(null); // Store the previous regionId
  const { accountUserTokenDetails } = useSelector((state) => state.ACCOUNT);
  // Fetch country list only if it's empty
  const fetchCountryList = async () => {
    if (countryList.length > 0 && previousRegionId.current === regionId) {
      return; // Avoid unnecessary API call
    }

    setLoading(true);
    setError(null);

    try {
      const langCode =
        accountUserTokenDetails?.langCode &&
        SUPPORTEDLANGCODES.includes(accountUserTokenDetails?.langCode)
          ? accountUserTokenDetails?.langCode
          : "en";

      const reqBody = { langCode };
      previousRegionId.current = regionId;
      // Fetch country codes
      const response = await billing.getCountryCodeCall(reqBody);

      if (response?.data?.data) {
        setCountryList(response.data.data); // Update the country list
      } else {
        setCountryList([]);
        setError("Received invalid data format for country codes.");
      }
    } catch (err) {
      console.log("Error fetching country codes:", err);
      setCountryList([]);
      setError("Failed to fetch country codes.");
    } finally {
      setLoading(false);
    }
  };

  // Memoize the value object to prevent unnecessary re-renders
  const contextValue = React.useMemo(
    () => ({
      countryList,
      loading,
      error,
      fetchCountryList,
    }),
    [countryList, loading, error, fetchCountryList]
  );

  return (
    <CountryListContext.Provider value={contextValue}>
      {children}
    </CountryListContext.Provider>
  );
};

// PropTypes validation
CountryListProvider.propTypes = {
  children: PropTypes.node.isRequired,
  regionId: PropTypes.any,
};